import { Trans, useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { LetterIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { AddBillEmailOption } from "./AddBillEmailVariantModalTypes";
import { AddBillEmailVariantSelectedOptionModal } from "./modals/AddBillEmailVariantSelectedOptionModal";

interface IAddBillEmailVariantModalProps extends IUseModalProps {
  onCancel?: () => void;
}

export const AddBillEmailVariantModal: React.FC<
  IAddBillEmailVariantModalProps
> = (props) => {
  const { onCancel, ...rest } = props;
  const modal = useModal(rest);
  const { create } = useModalActions();
  const { t } = useTranslation();

  const { data: customerDetails } = useCustomerDetailsQuery();

  const handleOptionClick = (option: AddBillEmailOption) => {
    create((modalProps) => (
      <AddBillEmailVariantSelectedOptionModal
        {...modalProps}
        billForwardEmail={customerDetails?.billForwardEmail ?? ""}
        variant={option}
        onClose={() =>
          create((modalProps) => <AddBillEmailVariantModal {...modalProps} />)
        }
      />
    ));

    modal.close();
  };

  return (
    <Modal
      modal={modal}
      title={t("addBillEmailVariantModal.title")}
      subtitle={
        <Trans
          i18nKey={"addBillEmailVariantModal.subtitle"}
          components={{
            span: <span className="font-semibold text-base-black" />,
          }}
          values={{ email: customerDetails?.billForwardEmail }}
        />
      }
      badgeProps={{
        icon: LetterIcon,
        color: "purple",
      }}
      className="md:max-w-xl"
      actionButtons={
        <div className="flex w-full justify-center">
          <Button
            className="w-[50%] bg-blue-light"
            variant="tertiary"
            onClick={() => {
              modal.close();
              onCancel?.();
            }}
          >
            {t("common.form.buttons.cancel")}
          </Button>
        </div>
      }
    >
      <div className="flex justify-center">
        <div className="flex max-w-[80%] flex-col gap-4">
          <Button
            className="w-full"
            onClick={() => handleOptionClick(AddBillEmailOption.SendToEmail)}
          >
            <span className="whitespace-normal">
              {t("addBillEmailVariantModal.sendToEmailOption.title")}
            </span>
          </Button>
          <Button
            className="w-full"
            onClick={() =>
              handleOptionClick(AddBillEmailOption.RedirectToEmail)
            }
          >
            <span className="whitespace-normal">
              {t("addBillEmailVariantModal.redirectToEmailOption.title")}
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};
